import { useState, useRef, useEffect } from "react";
import {
  MessageSquare,
  Mail,
  Building,
  Globe,
  FileText,
  Send,
  ArrowRight,
  AlertCircle,
  Loader2,
} from "lucide-react";
import ReCAPTCHA from "react-google-recaptcha";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import "../styles/Contact.scss";
import axios from 'axios';
import PrivacyPolicy from '../../src/lib/pdf/PrivacyPolicy.pdf'

// Define the API request type
interface SendContactParams {
  email: string;
  name: string;
  company?: string;
  website?: string;
  note?: string;
  recaptchaValue?: string;
}

// Function to send the contact form data
const sendContact = async (data: SendContactParams) => {
  const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/contacts`, {
    ...data,
    'g-recaptcha-response': data.recaptchaValue
  });

  if (response.status !== 200) {
    throw new Error('Error sending contact form');
  }

  return response.data;
};

const Contact = () => {
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    company: "",
    website: "",
    note: "",
  });

  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [websiteError, setWebsiteError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  
  // Add new states for form submission
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // ReCAPTCHA states and refs
  const [recaptchaValue, setRecaptchaValue] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  // New state to track last focused element
  const [lastFocusedField, setLastFocusedField] = useState<string | null>(null);
  
  const handleRecaptchaChange = (value: string | null) => {
    setRecaptchaValue(value);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));

    // Clear errors when user starts typing again
    if (id === "website" && websiteError) {
      setWebsiteError(null);
    }
    if (id === "email" && emailError) {
      setEmailError(null);
    }
    if (id === "name" && nameError) {
      setNameError(null);
    }
  };

  const validateWebsite = (url: string): boolean => {
    // Only validate if there's a value
    if (!url) return true;

    try {
      // Check if it's a valid URL format
      const urlObj = new URL(url);
      // Check if it has a valid protocol (http or https)
      return urlObj.protocol === "http:" || urlObj.protocol === "https:";
    } catch (e) {
      return false;
    }
  };

  const validateEmail = (email: string): boolean => {
    if (!email) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset all errors first
    setWebsiteError(null);
    setEmailError(null);
    setNameError(null);
    setSubmitError(null);
    
    // Validate form fields
    let hasErrors = false;
    
    // Validate required fields
    if (!formData.name.trim()) {
      setNameError("Please enter your name");
      hasErrors = true;
    }
    
    if (!formData.email.trim()) {
      setEmailError("Please enter your email address");
      hasErrors = true;
    } else if (!validateEmail(formData.email)) {
      setEmailError("Please enter a valid email address");
      hasErrors = true;
    }
    
    // Validate website URL
    if (!formData.website) {
      setWebsiteError("Please enter your website URL");
      hasErrors = true;
    } else if (!validateWebsite(formData.website)) {
      setWebsiteError("Please enter a valid URL (e.g., https://example.com)");
      hasErrors = true;
    }

    // Check if reCAPTCHA is completed
    if (!recaptchaValue) {
      setSubmitError("Please complete the reCAPTCHA verification.");
      hasErrors = true;
    }
    
    // Return early if any validation errors
    if (hasErrors) {
      // Set focus to the first field with an error
      if (nameError) {
        document.getElementById("name")?.focus();
      } else if (emailError) {
        document.getElementById("email")?.focus();
      } else if (websiteError) {
        document.getElementById("website")?.focus();
      }
      return;
    }

    // Continue with submission if no errors
    setSubmitSuccess(false);
    setIsSubmitting(true);

    try {
      // Prepare data for submission
      const contactData: SendContactParams = {
        email: formData.email,
        name: formData.name,
        company: formData.company || undefined,
        website: formData.website || undefined,
        note: formData.note || undefined,
        recaptchaValue: recaptchaValue || undefined
      };

      // Send the form data
      const response = await sendContact(contactData);
      
      // Handle success
      setSubmitSuccess(true);
      
      // Reset form after successful submission
      setFormData({
        email: "",
        name: "",
        company: "",
        website: "",
        note: "",
      });
      
      // Reset reCAPTCHA
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
        setRecaptchaValue(null);
      }
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSubmitSuccess(false);
      }, 5000);
      
    } catch (error) {
      // Handle errors
      console.error("Form submission error:", error);
      if (axios.isAxiosError(error) && error.response) {
        setSubmitError(error.response.data.message || "Failed to send your message. Please try again.");
      } else {
        setSubmitError("An unexpected error occurred. Please try again later.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Function to handle blur event on website field
  const handleWebsiteBlur = () => {
    setFocusedField(null);

    if (!formData.website) {
      setWebsiteError("Website URL is required");
    } else if (!validateWebsite(formData.website)) {
      setWebsiteError("Please enter a valid URL (e.g., https://example.com)");
    } else {
      setWebsiteError(null);
    }
  };
  
  // Function to handle blur event on email field
  const handleEmailBlur = () => {
    setFocusedField(null);
    
    if (!formData.email) {
      setEmailError("Email address is required");
    } else if (!validateEmail(formData.email)) {
      setEmailError("Please enter a valid email address");
    } else {
      setEmailError(null);
    }
  };
  
  // Function to handle blur event on name field
  const handleNameBlur = () => {
    setFocusedField(null);
    
    if (!formData.name.trim()) {
      setNameError("Name is required");
    } else {
      setNameError(null);
    }
  };

  // Handle focus event
  const handleFocus = (field: string) => {
    setFocusedField(field);
    setLastFocusedField(field);
  };

  // Handle form errors by returning focus to the problematic field
  useEffect(() => {
    if (websiteError && lastFocusedField === "website") {
      const websiteInput = document.getElementById("website");
      if (websiteInput) {
        websiteInput.focus();
      }
    }
  }, [websiteError, lastFocusedField]);

  // Handle recaptcha focus
  const handleRecaptchaFocus = () => {
    setFocusedField("recaptcha");
    setLastFocusedField("recaptcha");
  };
  
  // Handle submit error by focusing on the first input with error
  useEffect(() => {
    if (submitError) {
      if (!recaptchaValue) {
        // Focus recaptcha if that's the issue
        const recaptchaContainer = document.querySelector(".contact2__recaptcha");
        if (recaptchaContainer) {
          (recaptchaContainer as HTMLElement).focus();
        }
      } else {
        // Otherwise focus the first input
        const firstInput = document.getElementById("email");
        if (firstInput) {
          firstInput.focus();
        }
      }
    }
  }, [submitError, recaptchaValue]);

  return (
    <section
      id="contact"
      className="section contact2"
      aria-labelledby="contact-heading"
    >
      <div className="contact2__container">
        <header className="contact2__header">
          <div className="contact2__header-inner">
            <h2 id="contact-heading" className="contact2__title">
              Get in touch
            </h2>
            <p className="contact2__subtitle">
              Let's discuss how we can help make your website fully accessible
              and compliant with the latest standards.
            </p>
          </div>
        </header>

        <div className="contact2__form-container">
          {/* Contact form */}
          <div className="contact2__form-wrapper">
            {submitSuccess && (
              <div className="contact2__success-message" role="status" aria-live="polite">
                <p>Your message has been sent successfully! We'll get back to you soon.</p>
              </div>
            )}
            
            {submitError && (
              <div className="contact2__error-banner" role="alert" aria-live="assertive">
                <AlertCircle className="contact2__error-banner-icon" aria-hidden="true" />
                <p>{submitError}</p>
              </div>
            )}
            
            <form 
              className="contact2__form" 
              onSubmit={handleSubmit}
              noValidate
              aria-labelledby="contact-form-heading"
            >
              <div className="sr-only" id="contact-form-heading">Contact Form</div>
              
              <p className="contact2__form-instructions">
                Required fields are marked with <span aria-hidden="true">*</span><span className="sr-only">asterisk</span>
              </p>
              
              <div className="contact2__form-grid">
                <div
                  className={`contact2__form-group ${
                    focusedField === "email" ? "contact2__form-group--focused" : ""
                  } ${emailError ? "contact2__form-group--error" : ""}`}
                >
                  <label
                    htmlFor="email"
                    className="contact2__label"
                    id="email-label"
                  >
                    Email <span className="contact2__required" aria-hidden="true">*</span>
                    <span className="sr-only">(required)</span>
                  </label>
                  <div className="contact2__input-wrapper">
                    <div className="contact2__input-icon">
                      <Mail className="contact2__icon" aria-hidden="true" />
                    </div>
                    <Input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      onFocus={() => handleFocus("email")}
                      onBlur={handleEmailBlur}
                      className={`contact2__input ${emailError ? "contact2__input--error" : ""}`}
                      placeholder="your@email.com"
                      aria-required="true"
                      aria-invalid={emailError ? "true" : "false"}
                      aria-describedby={emailError ? "email-error" : undefined}
                      aria-labelledby="email-label"
                      disabled={isSubmitting}
                    />
                  </div>
                  {emailError && (
                    <div id="email-error" className="contact2__error-message" aria-live="polite">
                      <AlertCircle className="contact2__error-icon" aria-hidden="true" />
                      {emailError}
                    </div>
                  )}
                </div>

                <div
                  className={`contact2__form-group ${
                    focusedField === "name" ? "contact2__form-group--focused" : ""
                  } ${nameError ? "contact2__form-group--error" : ""}`}
                >
                  <label
                    htmlFor="name"
                    className="contact2__label"
                    id="name-label"
                  >
                    Name <span className="contact2__required" aria-hidden="true">*</span>
                    <span className="sr-only">(required)</span>
                  </label>
                  <div className="contact2__input-wrapper">
                    <div className="contact2__input-icon">
                      <MessageSquare className="contact2__icon" aria-hidden="true" />
                    </div>
                    <Input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      onFocus={() => handleFocus("name")}
                      onBlur={handleNameBlur}
                      className={`contact2__input ${nameError ? "contact2__input--error" : ""}`}
                      placeholder="Your Name"
                      aria-required="true"
                      aria-invalid={nameError ? "true" : "false"}
                      aria-describedby={nameError ? "name-error" : undefined}
                      aria-labelledby="name-label"
                      disabled={isSubmitting}
                    />
                  </div>
                  {nameError && (
                    <div id="name-error" className="contact2__error-message" aria-live="polite">
                      <AlertCircle className="contact2__error-icon" aria-hidden="true" />
                      {nameError}
                    </div>
                  )}
                </div>
              </div>

              <div className="contact2__form-grid">
                <div
                  className={`contact2__form-group ${
                    focusedField === "company" ? "contact2__form-group--focused" : ""
                  }`}
                >
                  <label
                    htmlFor="company"
                    className="contact2__label"
                    id="company-label"
                  >
                    Company
                  </label>
                  <div className="contact2__input-wrapper">
                    <div className="contact2__input-icon">
                      <Building className="contact2__icon" aria-hidden="true" />
                    </div>
                    <Input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      onFocus={() => handleFocus("company")}
                      onBlur={() => setFocusedField(null)}
                      className="contact2__input"
                      placeholder="Company Name"
                      aria-labelledby="company-label"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                <div
                  className={`contact2__form-group ${
                    focusedField === "website" ? "contact2__form-group--focused" : ""
                  } ${websiteError ? "contact2__form-group--error" : ""}`}
                >
                  <label
                    htmlFor="website"
                    className="contact2__label"
                    id="website-label"
                  >
                    Website <span className="contact2__required" aria-hidden="true">*</span>
                    <span className="sr-only">(required)</span>
                  </label>
                  <div className="contact2__input-wrapper">
                    <div className="contact2__input-icon">
                      <Globe className="contact2__icon" aria-hidden="true" />
                    </div>
                    <Input
                      type="url"
                      id="website"
                      name="website"
                      required
                      value={formData.website}
                      onChange={handleInputChange}
                      onFocus={() => handleFocus("website")}
                      onBlur={handleWebsiteBlur}
                      className={`contact2__input ${
                        websiteError ? "contact2__input--error" : ""
                      }`}
                      placeholder="https://yourwebsite.com"
                      aria-required="true"
                      aria-invalid={websiteError ? "true" : "false"}
                      aria-describedby={websiteError ? "website-error" : undefined}
                      aria-labelledby="website-label"
                      disabled={isSubmitting}
                    />
                  </div>
                  {websiteError && (
                    <div id="website-error" className="contact2__error-message" aria-live="polite">
                      <AlertCircle className="contact2__error-icon" aria-hidden="true" />
                      {websiteError}
                    </div>
                  )}
                </div>
              </div>

              <div
                className={`contact2__form-group ${
                  focusedField === "note" ? "contact2__form-group--focused" : ""
                }`}
              >
                <label
                  htmlFor="note"
                  className="contact2__label"
                  id="note-label"
                >
                  Note
                </label>
                <div className="contact2__input-wrapper">
                  <div className="contact2__textarea-icon">
                    <FileText className="contact2__icon" aria-hidden="true" />
                  </div>
                  <Textarea
                    id="note"
                    name="note"
                    rows={4}
                    value={formData.note}
                    onChange={handleInputChange}
                    onFocus={() => handleFocus("note")}
                    onBlur={() => setFocusedField(null)}
                    className="contact2__textarea"
                    placeholder="Tell us about your website..."
                    aria-labelledby="note-label"
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              {/* Consent text */}
              <div role="region" aria-labelledby="privacy-consent-heading" className="contact2__consent">
                <h3 id="privacy-consent-heading" className="sr-only">Privacy Consent</h3>
                <p className="contact2__consent-paragraph">
                  The processing of personal data takes place for the purpose of
                  handling your request. More information is available{" "}
                  <a
                    href={PrivacyPolicy}
                    className="contact2__link"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Read our Privacy Policy (opens in a new tab)"
                  >
                    in our Privacy Policy
                  </a>
                  .
                </p>

                <p className="contact2__consent-paragraph">
                  By clicking "GET IN TOUCH", you agree to our{" "}
                  <a
                    href={PrivacyPolicy}
                    className="contact2__link"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Read our Privacy Policy (opens in a new tab)"
                  >
                    Privacy Policy
                  </a>
                  . The personal information you provide, including your email,
                  name, company, website, and notes, will be securely stored for
                  up to six months. You may withdraw your consent at any time by
                  contacting us at privacy@mnb.solutions.
                </p>
              </div>

              {/* reCAPTCHA */}
              <div className="contact2__recaptcha-container">
                <div 
                  className="contact2__recaptcha" 
                  onFocus={handleRecaptchaFocus}
                  onBlur={() => setFocusedField(null)}
                  tabIndex={-1}
                >
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ''}
                    onChange={handleRecaptchaChange}
                    theme="light"
                    size="normal"
                    aria-label="reCAPTCHA verification"
                  />
                </div>
              </div>

              {/* Submit button */}
              <div className="contact2__submit-container">
                <Button
                  type="submit"
                  disabled={!recaptchaValue || isSubmitting}
                  className={`contact2__submit-button ${
                    recaptchaValue && !isSubmitting ? "contact2__submit-button--active" : "contact2__submit-button--disabled"
                  }`}
                  aria-disabled={!recaptchaValue || isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="contact2__submit-spinner" aria-hidden="true" />
                      <span>SENDING...</span>
                    </>
                  ) : (
                    <>
                      <span>GET IN TOUCH</span>
                      <ArrowRight className="contact2__submit-icon" aria-hidden="true" />
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact; 